import React from 'react';

// Card type display styling — kept here so both LevelUpModal and PostBattleScreen
// render card-pick choices identically.
const CARD_TYPE_COLORS = {
  attack: '#e74c3c', spell: '#9b59b6', block: '#3498db',
  skill: '#27ae60', power: '#e67e22', special: '#1abc9c',
};

const RARITY_COLORS = {
  common: '#888', uncommon: '#2ecc71', rare: '#3498db', epic: '#9b59b6', legendary: '#f1c40f',
};

/**
 * LevelUpCardPick — shared card-reward picker used by both PostBattleScreen
 * and the standalone LevelUpModal (catch-up path). Pure presentational
 * component: the parent owns selection/submit state.
 *
 * Props:
 *   - cardChoices: Array<Card> (3 cards from server)
 *   - forgeTokens: number — current Forge Token count, for the alt-pick card
 *   - selectedCard: string | null — key of selected card or '__forge_token__'
 *   - onSelect: (key | null) => void
 *   - onConfirm: () => void
 *   - onSkip: () => void
 *   - onBack?: () => void — optional "Back to Stats" link
 *   - isSubmitting?: boolean
 *   - error?: string | null
 *   - level?: number — for the badge
 *   - title?: string
 *   - subtitle?: string
 */
const LevelUpCardPick = ({
  cardChoices = [],
  forgeTokens = 0,
  selectedCard,
  onSelect,
  onConfirm,
  onSkip,
  onBack,
  isSubmitting = false,
  error = null,
  level,
  title = 'Choose a Card Reward',
  subtitle = 'This card will be permanently added to your combat deck. Choose wisely, or skip.',
}) => {
  return (
    <>
      <div className="pb-header levelup-header">
        <div className="pb-icon">🃏</div>
        <h2>{title}</h2>
        {level != null && <div className="level-badge">Level {level} Card Pick</div>}
      </div>

      <p style={{ color: '#aaa', textAlign: 'center', margin: '0.5rem 0 1rem', fontSize: '0.85rem' }}>
        {subtitle}
      </p>

      <div className="pb-cardpick-grid">
        {cardChoices.map((card, idx) => {
          const isSelected = selectedCard === card.key;
          const typeColor = CARD_TYPE_COLORS[card.type] || '#666';
          const rarityColor = RARITY_COLORS[card.rarity] || '#888';
          return (
            <div
              key={card.key}
              className={`pb-cardpick-card${isSelected ? ' pb-cardpick-selected' : ''}`}
              onClick={() => onSelect(isSelected ? null : card.key)}
              style={{
                borderTop: `4px solid ${typeColor}`,
                boxShadow: isSelected ? `0 4px 24px ${typeColor}66` : '0 2px 8px rgba(0,0,0,0.3)',
                animationDelay: `${idx * 0.08}s`,
              }}
            >
              <div className="pb-cardpick-top">
                <span className="pb-cardpick-cost">{card.apCost}</span>
                <span className="pb-cardpick-rarity" style={{ color: rarityColor }}>{card.rarity}</span>
              </div>
              <div className="pb-cardpick-name">{card.name}</div>
              <div className="pb-cardpick-type-wrap">
                <span className="pb-cardpick-type" style={{ background: `${typeColor}30`, color: typeColor }}>
                  {card.type}
                </span>
              </div>
              <div className="pb-cardpick-desc">{card.description || 'A powerful card.'}</div>
              {card.keywords && card.keywords.length > 0 && (
                <div className="pb-cardpick-keywords">
                  {card.keywords.map((kw, i) => (
                    <span key={i} className="pb-cardpick-keyword">{kw}</span>
                  ))}
                </div>
              )}
              {isSelected && <div className="pb-cardpick-check">✓ Selected</div>}
            </div>
          );
        })}

        {/* Forge Token alternative — equal visual weight, NOT buried */}
        {(() => {
          const isForgeSelected = selectedCard === '__forge_token__';
          return (
            <div
              className={`pb-cardpick-card pb-cardpick-forge${isForgeSelected ? ' pb-cardpick-selected' : ''}`}
              onClick={() => onSelect(isForgeSelected ? null : '__forge_token__')}
              style={{ animationDelay: `${cardChoices.length * 0.08}s` }}
            >
              <div className="pb-cardpick-forge-icon">⚒️</div>
              <div className="pb-cardpick-forge-name">Forge Token</div>
              <div className="pb-cardpick-forge-tag">Upgrade</div>
              <div className="pb-cardpick-desc">
                Skip a card and gain 1 Forge Token. Use tokens in the Deck Manager to upgrade your cards.
              </div>
              <div className="pb-cardpick-forge-count">
                You have: <strong>{forgeTokens}</strong> token{forgeTokens !== 1 ? 's' : ''}
              </div>
              {isForgeSelected && <div className="pb-cardpick-check pb-cardpick-check-forge">✓ Selected</div>}
            </div>
          );
        })()}
      </div>

      {error && <div className="pb-error">{error}</div>}

      <div className="pb-cardpick-actions">
        <button
          className={`pb-confirm-btn${selectedCard ? ' pb-confirm-ready' : ''}`}
          onClick={onConfirm}
          disabled={!selectedCard || isSubmitting}
        >
          {isSubmitting ? 'Leveling up...' : selectedCard === '__forge_token__' ? '⚒ Take Forge Token' : 'Add to Deck'}
        </button>
        <button
          className="pb-confirm-btn pb-cardpick-skip"
          onClick={onSkip}
          disabled={isSubmitting}
        >
          Skip Card
        </button>
      </div>

      {onBack && (
        <button className="pb-cardpick-back" onClick={onBack}>
          ← Back to Stats
        </button>
      )}
    </>
  );
};

export default LevelUpCardPick;
